import React, { useState, useMemo, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════════
// Tax Certificate Generation — Phase 1: Intake Wizard + Classification
// ═══════════════════════════════════════════════════════════════════

// ── Option definitions ───────────────────────────────────────────

const DEAL_TYPES = ['GO', 'Revenue', 'LOB', 'IFC', 'COP', 'GO Draw', 'LOB Draw'];
const FINANCING_TYPES = ['New Money', 'Refunding', 'Mixed'];
const YES_NO = ['Yes', 'No'];
const SALE_TYPES = ['Competitive', 'Negotiated', 'Bank Placement'];
const BQ_OPTIONS = ['Yes (Affirmative)', 'No-Ineligible', 'No-Not Designated', 'Section Absent'];
const STATES = ['NC', 'SC'];
const BOND_CATEGORIES = ['Governmental', 'Housing (§142(d))', '501(c)(3)', 'Exempt Facility'];
const ENTITY_TYPES = ['City', 'County', 'Town', 'Authority'];
const GOVERNING_BODY_OPTIONS = ['Board of Commissioners', 'City Council', 'Other'];
const DEFAULT_GOVERNING_BODY = {
  'City': 'City Council',
  'County': 'Board of Commissioners',
  'Town': 'Board of Commissioners',
  'Authority': 'Board of Directors',
};
const PREMIUM_DISCOUNT_LABELS = [
  'original issue premium',
  'original issue discount',
  'net original issue premium',
  'net original issue discount',
];

// Deal types where Corporation Present question is relevant
const CORP_DEAL_TYPES = ['LOB', 'LOB Draw', 'COP'];

// ── Fund type definitions for dynamic fund inventory ─────────────

const FUND_TYPES = [
  {
    key: 'debt_service',
    label: 'Debt Service Fund',
    nameOptions: ['Bond Fund', 'Debt Service Fund', 'Installment Payment Fund', 'Sinking Fund'],
  },
  {
    key: 'construction',
    label: 'Construction / Project Fund',
    nameOptions: ['Project Fund', 'Construction Fund', 'Acquisition Fund', 'Capital Projects Fund'],
  },
  {
    key: 'prepayment',
    label: 'Prepayment / Optional Redemption Fund',
    nameOptions: ['Prepayment Fund', 'Optional Redemption Fund'],
  },
  {
    key: 'rebate',
    label: 'Rebate Fund',
    nameOptions: ['Rebate Fund'],
  },
  {
    key: 'escrow',
    label: 'Escrow Fund',
    nameOptions: ['Escrow Fund', 'Escrow Account'],
    conditionalOn: 'ESCROW_STRUCTURE',
  },
  {
    key: 'acquisition_construction',
    label: 'Acquisition and Construction Fund',
    nameOptions: ['Acquisition and Construction Fund', 'A&C Fund'],
  },
];

// ── Auto-derivation helpers ─────────────────────────────────────

function deriveSpendingExceptions(financingType, dealType, drawDown) {
  if (!financingType) return '';
  const isIFC = dealType === 'IFC';
  if (financingType === 'Refunding') return '6M';
  if (financingType === 'Mixed') return '6M for refunding proceeds + 18M+2Y for NM proceeds';
  if (isIFC) return '6M+18M only';
  return '6M+18M+2Y';
}

// ── Date formatting helpers ──────────────────────────────────

function formatDateInput(raw) {
  // Auto-format as MM/DD/YYYY while user types — digits only, slashes inserted
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
}

function mmddyyyyToDisplay(val) {
  // Convert "MM/DD/YYYY" to "Month D, YYYY" for display / storage
  if (!val) return '';
  const m = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return val; // return as-is if not complete
  const date = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  if (isNaN(date.getTime())) return val;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function displayToMmddyyyy(display) {
  // Convert "Month D, YYYY" back to "MM/DD/YYYY" for the input field
  if (!display) return '';
  // Already in MM/DD/YYYY?
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(display)) return display;
  const d = new Date(display);
  if (isNaN(d.getTime())) return '';
  return String(d.getMonth() + 1).padStart(2, '0') + '/' +
         String(d.getDate()).padStart(2, '0') + '/' + d.getFullYear();
}

// ── Wizard step definitions ─────────────────────────────────────

const STEPS = [
  { id: 'state', label: 'State' },
  { id: 'deal-structure', label: 'Deal Structure' },
  { id: 'sale-parties', label: 'Sale & Parties' },
  { id: 'tax-provisions', label: 'Tax Provisions' },
  { id: 'refunding', label: 'Refunding' },
  { id: 'special', label: 'Special Provisions' },
  { id: 'deal-data', label: 'Deal Data' },
];

// ── Styles ──────────────────────────────────────────────────────

const styles = {
  container: {
    display: 'flex',
    height: 'calc(100vh - 52px)',
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    backgroundColor: 'var(--bg-primary)',
    overflow: 'hidden',
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  stepNav: {
    display: 'flex',
    gap: '4px',
    padding: '20px 32px 0',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 32px 24px',
    minHeight: 0,
  },
  rightPanel: {
    width: '340px',
    minWidth: '300px',
    borderLeft: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    overflowY: 'auto',
    padding: '20px',
    minHeight: 0,
  },
  stepBtn: (active) => ({
    padding: '6px 14px',
    borderRadius: '16px',
    border: `1px solid ${active ? 'var(--accent-color)' : 'var(--border-color)'}`,
    background: active ? 'var(--accent-color)' : 'transparent',
    color: active ? '#fff' : 'var(--text-secondary)',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: "'DM Sans', sans-serif",
  }),
  sectionTitle: { fontSize: '18px', fontWeight: 600, marginBottom: '20px', color: 'var(--text-primary)' },
  fieldGroup: { marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' },
  helpText: { fontSize: '11px', color: 'var(--text-tertiary)', fontStyle: 'italic', marginTop: '4px', lineHeight: 1.4 },
  select: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none' },
  input: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'vertical', minHeight: '60px', boxSizing: 'border-box' },
  autoTag: { display: 'inline-block', fontSize: '10px', fontWeight: 600, color: 'var(--accent-color)', backgroundColor: 'var(--accent-light)', padding: '2px 8px', borderRadius: '10px', marginLeft: '8px' },
  summaryTitle: { fontSize: '15px', fontWeight: 600, marginBottom: '14px', color: 'var(--text-primary)' },
  summaryGroup: { marginBottom: '12px' },
  summaryLabel: { fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '3px 0', borderBottom: '1px solid var(--border-light)' },
  summaryKey: { color: 'var(--text-secondary)' },
  summaryVal: (isAuto) => ({ fontWeight: 500, color: isAuto ? 'var(--accent-color)' : 'var(--text-primary)' }),
  generateBtn: { width: '100%', padding: '12px 0', borderRadius: '8px', border: 'none', background: 'var(--accent-color)', color: '#fff', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginTop: '20px' },
  generateBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  navBtns: { display: 'flex', gap: '12px', marginTop: '28px', paddingBottom: '20px' },
  navBtn: (primary) => ({ padding: '10px 24px', borderRadius: '6px', border: primary ? 'none' : '1px solid var(--border-color)', background: primary ? 'var(--accent-color)' : 'transparent', color: primary ? '#fff' : 'var(--text-secondary)', fontSize: '14px', fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }),
  divider: { height: '1px', background: 'var(--border-light)', margin: '16px 0' },
  resultBox: { marginTop: '16px', padding: '12px', borderRadius: '6px', backgroundColor: 'var(--bg-tertiary)', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' },
  fundRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)' },
  fundToggle: (on) => ({ width: '40px', height: '22px', borderRadius: '11px', border: 'none', background: on ? 'var(--accent-color)' : 'var(--border-color)', cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.15s ease' }),
  fundToggleKnob: (on) => ({ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: on ? '21px' : '3px', transition: 'left 0.15s ease' }),
  fundLabel: { flex: 1, fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' },
  fundNameSelect: { width: '220px', padding: '5px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" },
  infoNote: { padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--accent-light)', fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '12px' },
  preview: { fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '6px', padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: '4px', lineHeight: 1.5 },
};

// ── Reusable field components ────────────────────────────────────

function SelectField({ label, value, onChange, options, autoLabel, disabled, helpText }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>
        {label}
        {autoLabel && <span style={styles.autoTag}>AUTO: {autoLabel}</span>}
      </label>
      <select
        style={{ ...styles.select, ...(disabled ? { opacity: 0.6 } : {}) }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      {helpText && <p style={styles.helpText}>{helpText}</p>}
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', placeholder = '', note }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label}</label>
      {note && <p style={{ ...styles.helpText, marginTop: 0, marginBottom: '6px' }}>{note}</p>}
      <input
        type={type}
        style={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder = '' }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label}</label>
      <textarea
        style={styles.textarea}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function DateField({ label, value, onChange, note }) {
  const [rawInput, setRawInput] = useState(displayToMmddyyyy(value));

  const handleChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setRawInput(formatted);
    // When complete (MM/DD/YYYY), convert to "Month D, YYYY" for storage
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) {
      onChange(mmddyyyyToDisplay(formatted));
    } else if (formatted === '') {
      onChange('');
    }
  };

  // Sync rawInput when value changes externally
  React.useEffect(() => {
    const expected = displayToMmddyyyy(value);
    if (expected && expected !== rawInput) setRawInput(expected);
    if (!value) setRawInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label}</label>
      {note && <p style={{ ...styles.helpText, marginTop: 0, marginBottom: '6px' }}>{note}</p>}
      <input
        type="text"
        style={styles.input}
        value={rawInput}
        onChange={handleChange}
        placeholder="MM/DD/YYYY"
        maxLength={10}
      />
      {value && /^\w+ \d/.test(value) && <p style={styles.helpText}>{value}</p>}
    </div>
  );
}

function ToggleField({ label, value, onChange, helpText }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label}</label>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button type="button" style={styles.fundToggle(value)} onClick={() => onChange(!value)}>
          <div style={styles.fundToggleKnob(value)} />
        </button>
        <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{value ? 'Yes' : 'No'}</span>
      </div>
      {helpText && <p style={styles.helpText}>{helpText}</p>}
    </div>
  );
}

// ── Fund inventory row component ────────────────────────────────

function FundRow({ fund, entry, onChange }) {
  const [customName, setCustomName] = useState('');
  const isOn = entry.present;

  const handleToggle = () => {
    onChange({ present: !isOn, name: !isOn ? (fund.nameOptions[0] || '') : '' });
    setCustomName('');
  };

  const handleNameChange = (val) => {
    if (val === '__other__') {
      onChange({ present: true, name: customName || '' });
    } else {
      onChange({ present: true, name: val });
      setCustomName('');
    }
  };

  const isCustom = isOn && entry.name && !fund.nameOptions.includes(entry.name);

  return (
    <div style={styles.fundRow}>
      <button type="button" style={styles.fundToggle(isOn)} onClick={handleToggle}>
        <div style={styles.fundToggleKnob(isOn)} />
      </button>
      <span style={styles.fundLabel}>{fund.label}</span>
      {isOn && (
        <>
          <select
            style={styles.fundNameSelect}
            value={isCustom ? '__other__' : entry.name}
            onChange={(e) => handleNameChange(e.target.value)}
          >
            {fund.nameOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value="__other__">Other (free text)</option>
          </select>
          {isCustom && (
            <input
              type="text"
              style={{ ...styles.input, width: '160px', padding: '5px 8px', fontSize: '12px' }}
              value={entry.name}
              placeholder="Custom name"
              onChange={(e) => {
                setCustomName(e.target.value);
                onChange({ present: true, name: e.target.value });
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Summary panel component ─────────────────────────────────────

function SummaryPanel({ vars, data, funds, autoVars, goFlags, showCorpQuestion }) {
  const classificationItems = [
    'STATE', 'DEAL_TYPE', 'FINANCING_TYPE', 'DRAW_DOWN', 'YIELD_TYPE',
    'SALE_TYPE', 'TRUSTEE_PRESENT',
    ...(showCorpQuestion ? ['CORPORATION_PRESENT'] : []),
    'MULTI_SERIES',
    'MULTI_PROJECT', 'BOND_CATEGORY', 'OUTPUT_FACILITY',
    'BANK_QUALIFIED', 'BQ_SECTION_PRESENT', 'ESCROW_STRUCTURE',
    'REIMBURSEMENT', 'HTOP_MATURITIES',
    'UNDERSOLD_MATURITY', 'CALLABLE_EXCESS_PREMIUM', 'SPENDING_EXCEPTIONS',
    'CURRENT_REFUNDING', 'REFUNDING_PRIOR_CONTRACT', 'MULTIPURPOSE_ISSUE',
    'SCHOOL_DISTRICT_CERT',
  ];

  const activeFunds = Object.entries(funds).filter(([, v]) => v.present);

  return (
    <div>
      <div style={styles.summaryTitle}>Live Summary</div>

      <div style={styles.summaryGroup}>
        <div style={styles.summaryLabel}>Classification</div>
        {classificationItems.map((key) => {
          const val = vars[key];
          if (!val) return null;
          const isAuto = autoVars.has(key);
          return (
            <div key={key} style={styles.summaryRow}>
              <span style={styles.summaryKey}>{key}</span>
              <span style={styles.summaryVal(isAuto)}>
                {val}{isAuto ? ' (auto)' : ''}
              </span>
            </div>
          );
        })}
      </div>

      {(activeFunds.length > 0 || goFlags.go_debt_service_language || goFlags.go_project_fund_placeholder) && (
        <div style={styles.summaryGroup}>
          <div style={styles.summaryLabel}>Funds</div>
          {goFlags.go_debt_service_language && (
            <div style={styles.summaryRow}>
              <span style={styles.summaryKey}>debt_service</span>
              <span style={styles.summaryVal(true)}>"such account" form (auto)</span>
            </div>
          )}
          {activeFunds.map(([key, v]) => (
            <div key={key} style={styles.summaryRow}>
              <span style={styles.summaryKey}>{key}</span>
              <span style={styles.summaryVal(false)}>{v.name}</span>
            </div>
          ))}
          {goFlags.go_project_fund_placeholder && (
            <div style={styles.summaryRow}>
              <span style={styles.summaryKey}>construction</span>
              <span style={styles.summaryVal(true)}>placeholder (auto)</span>
            </div>
          )}
        </div>
      )}

      <div style={styles.summaryGroup}>
        <div style={styles.summaryLabel}>Deal Data</div>
        {Object.entries(data).map(([key, val]) => {
          if (!val) return null;
          return (
            <div key={key} style={styles.summaryRow}>
              <span style={styles.summaryKey}>{key}</span>
              <span style={styles.summaryVal(false)}>{val}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

const TaxCertificate = () => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showResultDetail, setShowResultDetail] = useState(false);

  // ── Classification variables ─────────────────────────────────
  const [vars, setVars] = useState({
    STATE: '',
    DEAL_TYPE: '',
    FINANCING_TYPE: '',
    DRAW_DOWN: '',
    YIELD_TYPE: '',
    SALE_TYPE: '',
    TRUSTEE_PRESENT: '',
    CORPORATION_PRESENT: '',
    MULTI_SERIES: '',
    MULTI_PROJECT: '',
    BOND_CATEGORY: '',
    OUTPUT_FACILITY: '',
    BANK_QUALIFIED: '',
    BQ_SECTION_PRESENT: '',
    ESCROW_STRUCTURE: '',
    REIMBURSEMENT: '',
    HTOP_MATURITIES: '',
    UNDERSOLD_MATURITY: '',
    CALLABLE_EXCESS_PREMIUM: '',
    SPENDING_EXCEPTIONS: '',
    CURRENT_REFUNDING: '',
    REFUNDING_PRIOR_CONTRACT: '',
    MULTIPURPOSE_ISSUE: '',
    SCHOOL_DISTRICT_CERT: '',
  });

  // ── Deal-specific data ─────────────────────────────────────
  const [data, setData] = useState({
    entity_type: '',
    issuer_name: '',
    issuer_state_designation: '',
    corporation_name: '',
    governing_body: '',
    governing_body_other: '',
    has_bond_order: false,
    bond_order_date: '',
    has_bond_resolution: false,
    bond_resolution_date: '',
    bond_name: '',
    series_designation: '',
    issue_date: '',
    sale_date: '',
    principal_amount: '',
    premium_or_discount_amount: '',
    premium_or_discount_label: '',
    bond_yield: '',
    underwriting_discount: '',
    first_interest_date: '',
    interest_payment_months: '',
    first_principal_date: '',
    final_maturity_date: '',
    project_description: '',
    coi_from_proceeds: true,
    coi_amount: '',
    project_fund_amount: '',
    has_financial_advisor: false,
    financial_advisor_name: '',
    has_transferred_proceeds: false,
    has_callable_bonds: false,
    callable_bond_maturities: '',
    first_call_date: '',
    reimbursement_amount: '',
    reimbursement_resolution_date: '',
    undersold_maturity_details: '',
  });

  // ── Underwriters / Lender + Trustee ────────────────────────
  const [underwriters, setUnderwriters] = useState(['']);
  const [trusteeName, setTrusteeName] = useState('');

  // ── Fund inventory ─────────────────────────────────────────
  const initFunds = {};
  FUND_TYPES.forEach((f) => { initFunds[f.key] = { present: false, name: '' }; });
  const [funds, setFunds] = useState(initFunds);

  // ── GO bond construction fund: named account question ──────
  const [goConstructionNamed, setGoConstructionNamed] = useState('');

  const setFund = useCallback((key, entry) => {
    setFunds((prev) => ({ ...prev, [key]: entry }));
  }, []);

  // ── Setters ──────────────────────────────────────────────────
  const setVar = useCallback((key, val) => {
    setVars((prev) => ({ ...prev, [key]: val }));
  }, []);

  const setField = useCallback((key, val) => {
    setData((prev) => ({ ...prev, [key]: val }));
  }, []);

  // ── Auto-derivations ────────────────────────────────────────
  const autoVars = useMemo(() => {
    const s = new Set();
    const next = { ...vars };

    if (vars.DRAW_DOWN === 'Yes') {
      next.YIELD_TYPE = 'Variable';
      s.add('YIELD_TYPE');
    }
    // Corporation auto-Yes for LOB / LOB Draw only
    if (vars.DEAL_TYPE === 'LOB' || vars.DEAL_TYPE === 'LOB Draw') {
      next.CORPORATION_PRESENT = 'Yes';
      s.add('CORPORATION_PRESENT');
    }
    if (vars.DEAL_TYPE === 'IFC' || vars.DEAL_TYPE === 'GO') {
      next.TRUSTEE_PRESENT = 'No';
      s.add('TRUSTEE_PRESENT');
    }

    // ESCROW_STRUCTURE: auto No when not Housing
    if (vars.BOND_CATEGORY && vars.BOND_CATEGORY !== 'Housing (§142(d))') {
      next.ESCROW_STRUCTURE = 'No';
      s.add('ESCROW_STRUCTURE');
    }

    const se = deriveSpendingExceptions(vars.FINANCING_TYPE, vars.DEAL_TYPE, vars.DRAW_DOWN);
    if (se) {
      next.SPENDING_EXCEPTIONS = se;
      s.add('SPENDING_EXCEPTIONS');
    }

    return { set: s, derived: next };
  }, [vars]);

  // Apply auto-derived values
  React.useEffect(() => {
    const { set: s, derived } = autoVars;
    setVars((prev) => {
      let updated = { ...prev };
      let changed = false;
      for (const key of s) {
        if (prev[key] !== derived[key]) {
          updated[key] = derived[key];
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [autoVars]);

  // ── Derived flags ───────────────────────────────────────────
  const isGO = vars.DEAL_TYPE === 'GO';
  const goDebtServiceLanguage = isGO;
  const goProjectFundPlaceholder = isGO && funds.construction.present && goConstructionNamed === 'No';
  const goFlags = { go_debt_service_language: goDebtServiceLanguage, go_project_fund_placeholder: goProjectFundPlaceholder };

  // Corporation question: only for LOB, LOB Draw, COP
  const showCorpQuestion = CORP_DEAL_TYPES.includes(vars.DEAL_TYPE);
  const isCorpAuto = vars.DEAL_TYPE === 'LOB' || vars.DEAL_TYPE === 'LOB Draw';

  // ── Conditional visibility helpers ──────────────────────────
  const showHTOP = vars.SALE_TYPE === 'Negotiated';
  const showUndersold = showHTOP && vars.HTOP_MATURITIES === 'Yes';
  const showCurrentRefunding = vars.FINANCING_TYPE === 'Refunding' || vars.FINANCING_TYPE === 'Mixed';
  const showRefundingPriorContract = showCurrentRefunding && vars.CURRENT_REFUNDING === 'Yes';
  const showMultipurpose = vars.FINANCING_TYPE === 'Mixed';
  const showCorporation = showCorpQuestion && vars.CORPORATION_PRESENT === 'Yes';
  const showReimbursementData = vars.REIMBURSEMENT === 'Yes';
  const isTrusteeAuto = vars.DEAL_TYPE === 'IFC' || vars.DEAL_TYPE === 'GO';
  const isYieldAuto = vars.DRAW_DOWN === 'Yes';

  // ── Submit handler ─────────────────────────────────────────
  const handleGenerate = async () => {
    setSubmitting(true);
    setResult(null);
    setShowResultDetail(false);
    try {
      const fundsPayload = {};
      Object.entries(funds).forEach(([k, v]) => {
        fundsPayload[k] = { present: v.present, name: v.name };
      });
      if (isGO) {
        fundsPayload.debt_service = { present: false, name: '' };
      }
      const payload = {
        ...vars,
        ...data,
        underwriters,
        trustee_name: trusteeName,
        funds: fundsPayload,
        go_debt_service_language: goDebtServiceLanguage,
        go_project_fund_placeholder: goProjectFundPlaceholder,
      };
      console.log('[TaxCert] Submitting payload:', payload);

      const resp = await fetch('/api/tax-certificate/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Tax_Certificate_${data.issuer_name || 'Draft'}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setResult({ success: true, message: 'Certificate downloaded successfully.' });
      } else {
        const err = await resp.json();
        setResult({ success: false, message: err.error || `HTTP ${resp.status}` });
      }
    } catch (err) {
      console.error('[TaxCert] Error:', err);
      setResult({ success: false, message: String(err) });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render steps ──────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <h3 style={styles.sectionTitle}>State Jurisdiction</h3>
            <SelectField
              label="State"
              value={vars.STATE}
              onChange={(v) => setVar('STATE', v)}
              options={STATES}
            />
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
              Determines governing law references, party descriptions, and state-specific language blocks.
            </p>
          </>
        );

      case 1:
        return (
          <>
            <h3 style={styles.sectionTitle}>Deal Structure</h3>
            <SelectField label="Deal Type" value={vars.DEAL_TYPE} onChange={(v) => setVar('DEAL_TYPE', v)} options={DEAL_TYPES} />
            <SelectField label="Bond Category" value={vars.BOND_CATEGORY} onChange={(v) => setVar('BOND_CATEGORY', v)} options={BOND_CATEGORIES} />
            <SelectField label="Financing Type" value={vars.FINANCING_TYPE} onChange={(v) => setVar('FINANCING_TYPE', v)} options={FINANCING_TYPES} />
            <SelectField label="Draw Down" value={vars.DRAW_DOWN} onChange={(v) => setVar('DRAW_DOWN', v)} options={YES_NO} />
            <SelectField label="Yield Type" value={vars.YIELD_TYPE} onChange={(v) => !isYieldAuto && setVar('YIELD_TYPE', v)} options={['Fixed', 'Variable']} autoLabel={isYieldAuto ? 'Variable (Draw Down = Yes)' : null} disabled={isYieldAuto} />
            <SelectField label="Multi-Series" value={vars.MULTI_SERIES} onChange={(v) => setVar('MULTI_SERIES', v)} options={YES_NO} />
            <SelectField label="Multi-Project" value={vars.MULTI_PROJECT} onChange={(v) => setVar('MULTI_PROJECT', v)} options={YES_NO} />
            {showMultipurpose && (
              <SelectField label="Multipurpose Issue" value={vars.MULTIPURPOSE_ISSUE} onChange={(v) => setVar('MULTIPURPOSE_ISSUE', v)} options={YES_NO} />
            )}

            <div style={styles.divider} />
            <ToggleField
              label="Callable Excess Premium Bonds"
              value={data.has_callable_bonds}
              onChange={(v) => setField('has_callable_bonds', v)}
            />
            {data.has_callable_bonds && (
              <>
                <TextField
                  label="Callable Bond Maturities"
                  value={data.callable_bond_maturities}
                  onChange={(v) => setField('callable_bond_maturities', v)}
                  placeholder="e.g., the 2031 through 2044 maturities"
                />
                <DateField
                  label="First Call Date"
                  value={data.first_call_date}
                  onChange={(v) => setField('first_call_date', v)}
                />
              </>
            )}
          </>
        );

      case 2:
        return (
          <>
            <h3 style={styles.sectionTitle}>Sale & Parties</h3>
            <SelectField label="Sale Type" value={vars.SALE_TYPE} onChange={(v) => setVar('SALE_TYPE', v)} options={SALE_TYPES} />
            <SelectField
              label="Trustee Present"
              value={vars.TRUSTEE_PRESENT}
              onChange={(v) => !isTrusteeAuto && setVar('TRUSTEE_PRESENT', v)}
              options={YES_NO}
              autoLabel={isTrusteeAuto ? `No (${vars.DEAL_TYPE})` : null}
              disabled={isTrusteeAuto}
            />
            {/* Corporation Present — only for LOB, LOB Draw, COP */}
            {showCorpQuestion && (
              <SelectField
                label="Corporation Present"
                value={vars.CORPORATION_PRESENT}
                onChange={(v) => !isCorpAuto && setVar('CORPORATION_PRESENT', v)}
                options={YES_NO}
                autoLabel={isCorpAuto ? `Yes (${vars.DEAL_TYPE})` : null}
                disabled={isCorpAuto}
              />
            )}

            <div style={styles.divider} />
            <ToggleField
              label="Financial Advisor"
              value={data.has_financial_advisor}
              onChange={(v) => setField('has_financial_advisor', v)}
            />
            {data.has_financial_advisor && (
              <TextField
                label="Financial Advisor Name"
                value={data.financial_advisor_name}
                onChange={(v) => setField('financial_advisor_name', v)}
                placeholder="e.g., Davenport & Company"
              />
            )}
          </>
        );

      case 3:
        return (
          <>
            <h3 style={styles.sectionTitle}>Tax Provisions</h3>
            <SelectField label="Output Facility" value={vars.OUTPUT_FACILITY} onChange={(v) => setVar('OUTPUT_FACILITY', v)} options={YES_NO} />
            <SelectField label="Bank Qualified" value={vars.BANK_QUALIFIED} onChange={(v) => setVar('BANK_QUALIFIED', v)} options={BQ_OPTIONS} />
            <SelectField label="BQ Section Present" value={vars.BQ_SECTION_PRESENT} onChange={(v) => setVar('BQ_SECTION_PRESENT', v)} options={YES_NO} />
            {vars.BOND_CATEGORY === 'Housing (§142(d))' && (
              <SelectField label="Escrow Structure" value={vars.ESCROW_STRUCTURE} onChange={(v) => setVar('ESCROW_STRUCTURE', v)} options={YES_NO} />
            )}
            <SelectField label="Reimbursement" value={vars.REIMBURSEMENT} onChange={(v) => setVar('REIMBURSEMENT', v)} options={YES_NO} />
            {showHTOP && <SelectField label="HTOP Maturities" value={vars.HTOP_MATURITIES} onChange={(v) => setVar('HTOP_MATURITIES', v)} options={YES_NO} />}
            {showUndersold && <SelectField label="Undersold Maturity" value={vars.UNDERSOLD_MATURITY} onChange={(v) => setVar('UNDERSOLD_MATURITY', v)} options={YES_NO} />}
            <SelectField label="Callable Excess Premium" value={vars.CALLABLE_EXCESS_PREMIUM} onChange={(v) => setVar('CALLABLE_EXCESS_PREMIUM', v)} options={YES_NO} />
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Spending Exceptions <span style={styles.autoTag}>AUTO</span></label>
              <input style={{ ...styles.input, opacity: 0.7 }} value={vars.SPENDING_EXCEPTIONS} readOnly />
            </div>
          </>
        );

      case 4:
        return (
          <>
            <h3 style={styles.sectionTitle}>Refunding Provisions</h3>
            {!showCurrentRefunding && (
              <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', padding: '20px 0' }}>
                Refunding provisions are only applicable when Financing Type includes Refunding.
                Set Financing Type to "Refunding" or "Mixed" in Step 2 to enable these fields.
              </p>
            )}
            {showCurrentRefunding && (
              <>
                <SelectField label="Current Refunding" value={vars.CURRENT_REFUNDING} onChange={(v) => setVar('CURRENT_REFUNDING', v)} options={YES_NO} />
                {showRefundingPriorContract && (
                  <SelectField label="Refunding Prior Contract" value={vars.REFUNDING_PRIOR_CONTRACT} onChange={(v) => setVar('REFUNDING_PRIOR_CONTRACT', v)} options={YES_NO} />
                )}
              </>
            )}
          </>
        );

      case 5:
        return (
          <>
            <h3 style={styles.sectionTitle}>Special Provisions</h3>
            <SelectField label="School District Certificate" value={vars.SCHOOL_DISTRICT_CERT} onChange={(v) => setVar('SCHOOL_DISTRICT_CERT', v)} options={YES_NO} />
          </>
        );

      case 6:
        return (
          <>
            <h3 style={styles.sectionTitle}>Deal-Specific Data</h3>

            {/* ── Issuer & Authorization ────────────────────────── */}
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px' }}>Issuer & Authorization</h4>
            <SelectField
              label="Entity Type"
              value={data.entity_type}
              onChange={(v) => {
                setField('entity_type', v);
                const defaultGov = DEFAULT_GOVERNING_BODY[v] || '';
                if (defaultGov) setField('governing_body', defaultGov);
              }}
              options={ENTITY_TYPES}
            />
            <TextField label="Issuer Name" value={data.issuer_name} onChange={(v) => setField('issuer_name', v)} placeholder={data.entity_type ? `e.g., ${data.entity_type} of Laurel` : 'e.g., City of Charlotte'} />
            <SelectField
              label="Issuer State"
              value={data.issuer_state_designation}
              onChange={(v) => setField('issuer_state_designation', v)}
              options={['North Carolina', 'South Carolina']}
              helpText="Appended to issuer name in the certificate (e.g., City of Laurel, North Carolina)"
            />
            {(data.issuer_name || data.issuer_state_designation) && (
              <div style={styles.preview}>
                {data.issuer_name && data.issuer_state_designation && (
                  <div>Full form: {data.issuer_name}, {data.issuer_state_designation}</div>
                )}
                {data.issuer_name && (
                  <div>Defined term: &ldquo;the {data.entity_type || 'Issuer'}&rdquo;</div>
                )}
              </div>
            )}

            {showCorporation && (
              <TextField label="Corporation Name" value={data.corporation_name} onChange={(v) => setField('corporation_name', v)} />
            )}

            <TextField
              label="Governing Body"
              value={data.governing_body}
              onChange={(v) => setField('governing_body', v)}
              placeholder="e.g., City Council, Board of Commissioners"
            />

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Authorizing Instruments</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={data.has_bond_order} onChange={(e) => setField('has_bond_order', e.target.checked)} />
                  Bond Order
                </label>
                {data.has_bond_order && (
                  <div style={{ marginLeft: '26px' }}>
                    <DateField label="Bond Order Date" value={data.bond_order_date} onChange={(v) => setField('bond_order_date', v)} />
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={data.has_bond_resolution} onChange={(e) => setField('has_bond_resolution', e.target.checked)} />
                  Bond Resolution
                </label>
                {data.has_bond_resolution && (
                  <div style={{ marginLeft: '26px' }}>
                    <DateField label="Bond Resolution Date" value={data.bond_resolution_date} onChange={(v) => setField('bond_resolution_date', v)} />
                  </div>
                )}
              </div>
            </div>

            <div style={styles.divider} />

            {/* ── Parties & Original Purchaser ──────────────────── */}
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px' }}>Parties & Original Purchaser</h4>
            {(() => {
              const isIFC = vars.DEAL_TYPE === 'IFC';
              const isNegotiated = vars.SALE_TYPE === 'Negotiated';
              const isCompetitive = vars.SALE_TYPE === 'Competitive';
              const isBankPlacement = vars.SALE_TYPE === 'Bank Placement';
              const showTrustee = vars.TRUSTEE_PRESENT === 'Yes';

              let fieldLabel = 'Underwriter / Lender';
              if (isIFC) fieldLabel = 'Lender';
              else if (isBankPlacement) fieldLabel = 'Lender';
              else if (isCompetitive) fieldLabel = 'Winning Bidder / Underwriter';
              else if (isNegotiated) fieldLabel = 'Underwriter(s)';

              const isMulti = isNegotiated && !isIFC;

              return (
                <>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>{fieldLabel}</label>
                    {isMulti ? (
                      <>
                        {underwriters.map((uw, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                            <input
                              style={{ ...styles.input, flex: 1 }}
                              value={uw}
                              placeholder={idx === 0 ? 'Senior Managing Underwriter' : `Co-Manager ${idx}`}
                              onChange={(e) => {
                                const next = [...underwriters];
                                next[idx] = e.target.value;
                                setUnderwriters(next);
                              }}
                            />
                            {underwriters.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setUnderwriters(underwriters.filter((_, i) => i !== idx))}
                                style={{ background: 'none', border: 'none', color: 'var(--error-color)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px' }}
                                title="Remove"
                              >&times;</button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setUnderwriters([...underwriters, ''])}
                          style={{ background: 'none', border: '1px dashed var(--border-color)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', color: 'var(--accent-color)', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
                        >+ Add Underwriter</button>
                      </>
                    ) : (
                      <input
                        style={styles.input}
                        value={underwriters[0] || ''}
                        onChange={(e) => setUnderwriters([e.target.value])}
                      />
                    )}
                  </div>
                  {showTrustee && (
                    <TextField label="Trustee" value={trusteeName} onChange={(v) => setTrusteeName(v)} />
                  )}
                </>
              );
            })()}

            <div style={styles.divider} />

            {/* ── Bond Details ─────────────────────────────────── */}
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px' }}>Bond Details</h4>
            <TextField
              label="Bond / Obligation Name"
              value={data.bond_name}
              onChange={(v) => setField('bond_name', v)}
              placeholder="e.g., General Obligation Bonds"
              note='Enter the base name without series (e.g., "General Obligation Bonds"). The series designation is entered separately below.'
            />
            <TextField label="Series Designation" value={data.series_designation} onChange={(v) => setField('series_designation', v)} placeholder="e.g., Series 2026A" />
            {(data.bond_name || data.series_designation) && (
              <div style={styles.preview}>
                {data.bond_name && data.series_designation && (
                  <div>Full form: {data.bond_name}, {data.series_designation}</div>
                )}
                {data.bond_name && (
                  <div>Short form: {data.bond_name}</div>
                )}
              </div>
            )}

            <div style={styles.divider} />

            {/* ── Dates & Amounts ──────────────────────────────── */}
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px' }}>Dates & Amounts</h4>
            <DateField label="Issue Date" value={data.issue_date} onChange={(v) => setField('issue_date', v)} />
            <DateField label="Sale Date" value={data.sale_date} onChange={(v) => setField('sale_date', v)} />
            <TextField label="Principal Amount" value={data.principal_amount} onChange={(v) => setField('principal_amount', v)} placeholder="$" />
            <TextField
              label="Premium / Discount Amount"
              value={data.premium_or_discount_amount}
              onChange={(v) => setField('premium_or_discount_amount', v)}
              placeholder="$ (negative for discount)"
            />
            <SelectField
              label="Premium / Discount Label"
              value={data.premium_or_discount_label}
              onChange={(v) => setField('premium_or_discount_label', v)}
              options={PREMIUM_DISCOUNT_LABELS}
            />
            {/* Computed Issue Price (read-only) */}
            {(() => {
              const pa = parseFloat((data.principal_amount || '0').replace(/[$,\s]/g, '')) || 0;
              const pd = parseFloat((data.premium_or_discount_amount || '0').replace(/[$,\s]/g, '')) || 0;
              const ip = pa + pd;
              return ip > 0 ? (
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Issue Price <span style={styles.autoTag}>COMPUTED</span></label>
                  <input style={{ ...styles.input, opacity: 0.7 }} value={'$' + ip.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} readOnly />
                  {data.premium_or_discount_label && <p style={styles.helpText}>= principal {pd >= 0 ? '+' : '−'} {data.premium_or_discount_label}</p>}
                </div>
              ) : null;
            })()}
            <TextField label="Bond Yield (or Computation Period yield)" value={data.bond_yield} onChange={(v) => setField('bond_yield', v)} placeholder="e.g., 3.25%" />
            <TextField label="Underwriting Discount" value={data.underwriting_discount} onChange={(v) => setField('underwriting_discount', v)} placeholder="$" />

            <div style={styles.divider} />

            {/* ── Interest Payment Details ─────────────────────── */}
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px' }}>Interest & Maturity</h4>
            <DateField label="First Interest Date" value={data.first_interest_date} onChange={(v) => setField('first_interest_date', v)} />
            <TextField
              label="Interest Payment Months"
              value={data.interest_payment_months}
              onChange={(v) => setField('interest_payment_months', v)}
              placeholder="e.g., May 1 and November 1"
            />
            <DateField label="First Principal Date" value={data.first_principal_date} onChange={(v) => setField('first_principal_date', v)} />
            <DateField label="Final Maturity Date" value={data.final_maturity_date} onChange={(v) => setField('final_maturity_date', v)} />

            <div style={styles.divider} />

            {/* ── Use of Proceeds ──────────────────────────────── */}
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px' }}>Use of Proceeds</h4>
            <TextAreaField label="Project Description" value={data.project_description} onChange={(v) => setField('project_description', v)} />

            <ToggleField
              label="Costs of Issuance from Proceeds"
              value={data.coi_from_proceeds}
              onChange={(v) => setField('coi_from_proceeds', v)}
            />
            {data.coi_from_proceeds && (
              <>
                <TextField label="Costs of Issuance Amount" value={data.coi_amount} onChange={(v) => setField('coi_amount', v)} placeholder="$" />
                <TextField label="Project Fund Amount" value={data.project_fund_amount} onChange={(v) => setField('project_fund_amount', v)} placeholder="$" />
              </>
            )}

            <ToggleField
              label="Transferred Proceeds"
              value={data.has_transferred_proceeds}
              onChange={(v) => setField('has_transferred_proceeds', v)}
              helpText="Whether the issue has transferred proceeds from a prior issue"
            />

            {showReimbursementData && (
              <>
                <div style={styles.divider} />
                <TextField label="Reimbursement Amount" value={data.reimbursement_amount} onChange={(v) => setField('reimbursement_amount', v)} placeholder="$" />
                <DateField label="Reimbursement Resolution Date" value={data.reimbursement_resolution_date} onChange={(v) => setField('reimbursement_resolution_date', v)} />
              </>
            )}
            {showUndersold && (
              <>
                <div style={styles.divider} />
                <TextAreaField label="Undersold Maturity Details" value={data.undersold_maturity_details} onChange={(v) => setField('undersold_maturity_details', v)} />
              </>
            )}

            {/* ── Fund Inventory ───────────────────────────────── */}
            <div style={styles.divider} />
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px', marginBottom: '14px' }}>Fund Inventory</h3>

            {isGO && (
              <div style={styles.infoNote}>
                General Obligation Bond — debt service language will use "such account, if created" form (no named fund required)
              </div>
            )}

            {FUND_TYPES.map((fund) => {
              if (fund.conditionalOn && vars[fund.conditionalOn] !== 'Yes') return null;
              if (isGO && fund.key === 'debt_service') return null;

              if (isGO && fund.key === 'construction') {
                return (
                  <div key={fund.key}>
                    <FundRow
                      fund={fund}
                      entry={funds[fund.key]}
                      onChange={(entry) => {
                        setFund(fund.key, entry);
                        if (!entry.present) setGoConstructionNamed('');
                      }}
                    />
                    {funds.construction.present && (
                      <div style={{ marginLeft: '52px', marginTop: '-6px', marginBottom: '12px' }}>
                        <SelectField
                          label="Is there a specifically named account for construction proceeds?"
                          value={goConstructionNamed}
                          onChange={(v) => {
                            setGoConstructionNamed(v);
                            if (v === 'No') {
                              setFund('construction', { present: true, name: '' });
                            } else if (v === 'Yes' && !funds.construction.name) {
                              setFund('construction', { present: true, name: fund.nameOptions[0] });
                            }
                          }}
                          options={YES_NO}
                        />
                        {goConstructionNamed === 'No' && (
                          <div style={styles.infoNote}>
                            Sale proceeds will be defined as deposited to a segregated account designated as the Project Fund (placeholder language)
                          </div>
                        )}
                        {goConstructionNamed === 'Yes' && (
                          <div style={{ marginTop: '8px' }}>
                            <FundRow fund={fund} entry={funds[fund.key]} onChange={(entry) => setFund(fund.key, entry)} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <FundRow key={fund.key} fund={fund} entry={funds[fund.key]} onChange={(entry) => setFund(fund.key, entry)} />
              );
            })}

            <div style={styles.divider} />
            <button
              style={{ ...styles.generateBtn, ...(submitting ? styles.generateBtnDisabled : {}) }}
              onClick={handleGenerate}
              disabled={submitting}
            >
              {submitting ? 'Generating...' : 'Generate Certificate'}
            </button>
            {result && (
              <div style={{ marginTop: 16, padding: 12, borderRadius: 6, border: '1px solid #ccc', background: '#f9f9f9', color: '#333', whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 400, overflowY: 'auto' }}>
                {JSON.stringify(result, null, 2)}
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.leftPanel}>
        <div style={styles.stepNav}>
          {STEPS.map((s, i) => (
            <button key={s.id} style={styles.stepBtn(i === step)} onClick={() => setStep(i)}>
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
        <div style={styles.stepContent}>
          {renderStep()}
          <div style={styles.navBtns}>
            {step > 0 && <button style={styles.navBtn(false)} onClick={() => setStep(step - 1)}>Back</button>}
            {step < STEPS.length - 1 && <button style={styles.navBtn(true)} onClick={() => setStep(step + 1)}>Next</button>}
          </div>
        </div>
      </div>
      <div style={styles.rightPanel}>
        <SummaryPanel vars={vars} data={data} funds={funds} autoVars={autoVars.set} goFlags={goFlags} showCorpQuestion={showCorpQuestion} />
      </div>
    </div>
  );
};

export default TaxCertificate;
